/**
 * Escena animada del panel lateral del ingreso: un representante recorre la ruta del día, cada cliente se
 * marca al visitarlo y salen avisos de abonos, trabajo sin señal y sincronización.
 *
 * Usa el color de la marca de la empresa (`primary`). Solo se ve desde tableta; con «reducir movimiento» del
 * sistema queda quieta con la ruta completa. Clientes ficticios.
 */
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useT } from '../i18n';

const NAVY = '#0B2545';
const ROUTE = 'M120 620 L120 470 L300 470 L300 330 L480 330 L480 200 L620 200';
/** Paradas y en qué parte del recorrido (0 a 1) llega el representante; viaja el 83,3 % de los 12 s. */
const STOPS = [
  {
    x: 120,
    y: 470,
    at: 0.163,
    name: 'Ana R.',
    lx: -14,
    ly: -16,
    anchor: 'end',
  },
  {
    x: 300,
    y: 470,
    at: 0.359,
    name: 'Don Luis',
    lx: 0,
    ly: 34,
    anchor: 'start',
  },
  {
    x: 300,
    y: 330,
    at: 0.511,
    name: 'Marta G.',
    lx: -16,
    ly: -16,
    anchor: 'end',
  },
  {
    x: 480,
    y: 330,
    at: 0.707,
    name: 'Tienda La 14',
    lx: 16,
    ly: 30,
    anchor: 'start',
    warn: true,
  },
  {
    x: 480,
    y: 200,
    at: 0.848,
    name: 'Jorge P.',
    lx: -16,
    ly: -16,
    anchor: 'end',
  },
  { x: 620, y: 200, at: 1, name: 'Rosa M.', lx: 0, ly: 36, anchor: 'middle' },
];
const BLOCKS: [number, number, number, number][] = [];
for (const [x, w] of [
  [40, 70],
  [130, 160],
  [310, 160],
  [490, 160],
]) {
  for (const [y, h] of [
    [100, 90],
    [210, 110],
    [340, 120],
    [480, 130],
  ])
    BLOCKS.push([x, y, w, h]);
}

const pinKeyframes = STOPS.map((s, i) => {
  const p = Math.min(s.at * 83.3, 82);
  return `@keyframes rs-p${i}{0%,${p.toFixed(1)}%{fill:#29507A}${(p + 1.2).toFixed(1)}%,96%{fill:var(--rs-${s.warn ? 'warn' : 'accent'})}100%{fill:#29507A}}`;
}).join('');

const CSS = `
@keyframes rs-draw{0%{stroke-dashoffset:920}83.3%,96%{stroke-dashoffset:0}100%{stroke-dashoffset:920}}
@keyframes rs-halo{0%{transform:scale(1);opacity:.5}100%{transform:scale(2.6);opacity:0}}
@keyframes rs-t1{0%,30%{opacity:0;transform:translateY(10px)}33%,44%{opacity:1;transform:none}47%,100%{opacity:0;transform:translateY(-10px)}}
@keyframes rs-t2{0%,47%{opacity:0;transform:translateY(10px)}50%,59%{opacity:1;transform:none}62%,100%{opacity:0;transform:translateY(-10px)}}
@keyframes rs-t3{0%,62%{opacity:0;transform:translateY(10px)}65%,82%{opacity:1;transform:none}85%,100%{opacity:0;transform:translateY(-10px)}}
${pinKeyframes}
.rs-halo{transform-box:fill-box;transform-origin:center;animation:rs-halo 1.6s ease-out infinite}
.rs-toast{position:absolute;inset:0 auto auto 0;opacity:0}
/* Pantallas bajas (teléfono acostado): sin panel lateral, solo el formulario a todo el ancho. */
@media (max-height: 679.98px){div:has(> .rs-scene){display:none!important}div:has(> div > .rs-scene){grid-template-columns:minmax(0,1fr)!important}}
@media (prefers-reduced-motion: reduce){.rs-scene *{animation:none!important}.rs-scene .rs-toast:first-child{opacity:1}}`;

export const RouteScene: React.FC = () => {
  const t = useT();
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const still = useMediaQuery('(prefers-reduced-motion: reduce)');
  const toasts = [
    { color: '#1A7A6E', text: t('loginScene.payment') },
    { color: '#C47F17', text: t('loginScene.offline') },
    { color: '#1A7A6E', text: t('loginScene.synced') },
  ];
  return (
    <Box
      className="rs-scene"
      style={{
        ['--rs-accent' as any]: accent,
        ['--rs-warn' as any]: '#E3A13B',
      }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minWidth: 0,
        bgcolor: NAVY,
        color: '#F3F6F9',
        backgroundImage: 'radial-gradient(#29507A 1.2px, transparent 1.3px)',
        backgroundSize: '24px 24px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <style>{CSS}</style>
      <Box sx={{ position: 'relative', flex: '1 1 auto', minHeight: 0 }}>
        <Box
          component="svg"
          viewBox="0 60 700 600"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 96,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            maxWidth: 900,
            height: 'calc(100% - 96px)',
          }}
        >
          <g fill="#0F3358">
            {BLOCKS.map(([x, y, w, h], i) => (
              <rect key={i} x={x} y={y} width={w} height={h} rx={8} fill={i === 5 ? '#11402F' : undefined} />
            ))}
          </g>
          <path d={ROUTE} fill="none" stroke="#29507A" strokeWidth={4} strokeDasharray="2 10" strokeLinecap="round" />
          <path
            d={ROUTE}
            fill="none"
            stroke={accent}
            strokeWidth={5}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={920}
            style={{
              animation: 'rs-draw 12s linear infinite',
              strokeDashoffset: still ? 0 : undefined,
            }}
          />
          <circle cx={120} cy={620} r={7} fill="#E6EBF1" />
          {STOPS.map((s, i) => (
            <g key={s.name}>
              <circle
                cx={s.x}
                cy={s.y}
                r={10}
                stroke={NAVY}
                strokeWidth={3}
                style={{
                  fill: still ? (s.warn ? '#E3A13B' : accent) : '#29507A',
                  animation: `rs-p${i} 12s linear infinite`,
                }}
              />
              <text x={s.x + s.lx} y={s.y + s.ly} textAnchor={s.anchor} fontSize={13} fill="#B7C2CF" fontFamily="inherit">
                {s.name}
              </text>
            </g>
          ))}
          <g transform={still ? 'translate(620 200)' : undefined}>
            {!still && <animateMotion dur="12s" repeatCount="indefinite" keyPoints="0;1;1" keyTimes="0;0.833;1" calcMode="linear" path={ROUTE} />}
            <circle className="rs-halo" r={14} fill={accent} />
            <circle r={12} fill="#F3F6F9" stroke={accent} strokeWidth={4} />
          </g>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            left: { md: 40, lg: 56 },
            right: { md: 40, lg: 56 },
            top: 32,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              bgcolor: 'rgba(14,46,80,.92)',
              border: '1px solid #29507A',
              borderRadius: 999,
              px: 1.75,
              py: 1,
              fontSize: 13,
              color: '#C9D2DC',
            }}
          >
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent }} />
            {t('loginScene.route')}
          </Box>
          <Box
            sx={{
              position: 'relative',
              flex: '1 1 260px',
              height: 48,
              minWidth: 0,
            }}
          >
            {toasts.map((x, i) => (
              <Box
                key={x.text}
                className="rs-toast"
                style={{ animation: `rs-t${i + 1} 12s ease-in-out infinite` }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  bgcolor: '#F7F4EC',
                  color: '#14202C',
                  borderRadius: 3,
                  px: 2,
                  py: 1.5,
                  fontSize: 14,
                  boxShadow: '0 16px 40px rgba(0,0,0,.35)',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: x.color,
                    flex: 'none',
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {x.text}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          px: { md: 5, lg: 7 },
          pb: { md: 5, lg: 6.5 },
          pt: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography
          component="p"
          sx={{
            fontSize: { md: 22, lg: 26 },
            fontWeight: 700,
            lineHeight: 1.25,
            maxWidth: 520,
            textWrap: 'balance',
          }}
        >
          {t('loginScene.caption')}
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#9FB3C8' }}>{t('loginScene.note')}</Typography>
      </Box>
    </Box>
  );
};
